import { DuplicateRecordsForId, DuplicateSelectionsMap, FileRecord } from '@amzn/awscat-portfolio-assessment-types';
import { Grid, Table, TableProps } from '@amzn/awsui-components-react';
import React, { ReactChild } from 'react';

import { RecordColCell } from '../common-components/ColumnDefinitions';
import { DuplicateProperty, PropertiesConfigMap } from '../common-util/types';
import { setDuplicateSelectionForId } from './DuplicateHelpers';

/**
 * Pretty-print composite id
 * @param id
 * @param propertiesConfigMap
 */
function prettyPrintId(id: string, propertiesConfigMap: PropertiesConfigMap): string {
    try {
        // if parsing succeeded, then id is a composite key
        const ids: {[id: string]: string} = JSON.parse(id);
        // pretty print the composite key
        id = Object.keys(ids).map((idProperty: string) => {
            const label = propertiesConfigMap.get(idProperty)?.label;
            return `${label}: ${ids[idProperty]}`;
        }).join(', ');
    } catch (err) {
        // if parsing failed, then id is a single key, return the original key
    }
    return id;
}

/**
 * A card that displays all duplicate records associated with a specific ID in a table format
 * @param duplicateSelectionsMap
 * @param onDuplicateSelectionsChange
 * @param duplicateRecordsForId
 * @param id
 * @param propertiesConfigMap
 * @param diffOnly
 */
export const DuplicateCardForId = (duplicateSelectionsMap: DuplicateSelectionsMap,
    onDuplicateSelectionsChange: (duplicateSelectionsMap: DuplicateSelectionsMap) => void,
    duplicateRecordsForId: DuplicateRecordsForId,
    id: string,
    propertiesConfigMap: PropertiesConfigMap,
    diffOnly: boolean): ReactChild | null => {
    const selectedIndex: number = duplicateSelectionsMap[id];
    if (!duplicateRecordsForId?.records?.length || !Array.isArray(duplicateRecordsForId?.properties)) {
        return null;
    }
    const columnDefinitions: TableProps.ColumnDefinition<FileRecord>[] = duplicateRecordsForId.properties
        .filter((propertyObj: DuplicateProperty) => {
            return (!diffOnly || propertyObj.diff) && propertiesConfigMap.get(propertyObj.property)?.label;
        })
        .map((propertyObj: DuplicateProperty) => {
            const header = propertiesConfigMap.get(propertyObj.property)?.label;
            const className = propertyObj.diff ? 'header-with-diff' : '';
            return {
                cell: (item: FileRecord) => RecordColCell(item, propertyObj.property),
                header: <span className={className}>{header}</span>,
                id: propertyObj.property,
            };
        });

    const items: FileRecord[] = duplicateRecordsForId.records.map((record: FileRecord, index: number) => {
        return {...record, index, id};
    });

    const duplicateIdWrapperClassName: string = (selectedIndex == null) ? ' duplicate-id-wrapper-not-selected' : ' duplicate-id-wrapper-selected';
    const duplicateIdClassName: string = (selectedIndex == null) ? ' duplicate-id-not-selected' : '';

    const onSelectionChange = (e: any): void => {
        const selectedRecord = e.detail.selectedItems?.[0];
        const duplicateSelectionsMapCopy = {...duplicateSelectionsMap};
        setDuplicateSelectionForId(duplicateSelectionsMapCopy, selectedRecord.id, selectedRecord.index);
        onDuplicateSelectionsChange(duplicateSelectionsMapCopy);
    };
    return (
        <Grid gridDefinition={[{colspan: 2}, {colspan: 10}]} disableGutters>
            <div className='horizontal-scroll'>
                <div className={'duplicate-id-wrapper' + duplicateIdWrapperClassName}>
                    <h3 className={'duplicate-id' + duplicateIdClassName}>{prettyPrintId(duplicateRecordsForId.id, propertiesConfigMap)}</h3>
                </div>
            </div>
            <Table
                items={items}
                columnDefinitions={columnDefinitions}
                selectionType='single'
                onSelectionChange={onSelectionChange}
                selectedItems={[items[selectedIndex]]}
            />
        </Grid>
    );
};
